<template>
  <v-layout>
    <profile-navigation />

    <v-layout column justify-start v-if="id" style="margin-left: 49px">
      <v-row v-if="showBackButton" align="center" no-gutters class="mb-3">
        <div class="title text--secondary mt-2 px-4 text-uppercase">
          {{ $t("profile.employeeTitle") }}
        </div>
        <div>
          <back-button />
        </div>
      </v-row>

      <v-row id="profileOverview" align="start" no-gutters>
        <v-col
          cols="12"
          lg="8"
          xl="6"
          :class="$vuetify.breakpoint.lgAndUp ? 'mr-3' : ''"
        >
          <employee-profile-data
            v-if="employee.id"
            :id="+id"
            :employee="employee"
            @refresh="$apollo.queries.employee.refresh()"
            @showHistoryChange="(state) => (history = state)"
          />
          <v-skeleton-loader v-else type="card" />
        </v-col>
        <v-col
          class="text-end"
          :style="$vuetify.breakpoint.lgAndUp ? 'height: 100%' : ''"
          :class="$vuetify.breakpoint.mdAndDown ? 'mt-3' : ''"
        >
          <employee-alerts :employee="employee" :riskAnalyses="riskAnalyses" />
        </v-col>
      </v-row>

      <template v-if="!history">
        <template v-if="employee.tags">
          <v-row
            id="profileMentorships"
            align="center"
            style="max-height: 56px"
            class="mt-2 mb-1"
          >
            <v-col cols="auto">
              <div class="title text--secondary text-uppercase">
                {{ $t("profile.subheader.mentorship") }}<mentorship-info />
              </div>
            </v-col>
            <unstarted-mentorships
              v-if="
                role(
                  'admin',
                  'departmentManager',
                  'manager',
                  'safetyRepresentative'
                )
              "
              :employee="employee"
              @refresh="$apollo.queries.employee.refresh()"
            />
          </v-row>
          <employee-mentorship
            :id="id"
            :employee="employee"
            :mentorships="employee.mentorships"
            :sortByCategoryLocation="true"
            @refresh="$apollo.queries.employee.refresh()"
          />
        </template>

        <v-row id="profileInstructions" align="center" class="mt-3">
          <v-col cols="auto">
            <span class="title text--secondary text-uppercase">{{
              $t("profile.subheader.instructions")
            }}</span>
          </v-col>

          <v-col cols="auto">
            <v-autocomplete
              v-if="
                role(
                  'admin',
                  'departmentManager',
                  'manager',
                  'safetyRepresentative'
                )
              "
              v-model="instructionGroupFilter"
              :items="instructionGroups"
              multiple
              dense
              item-text="name"
              :label="$t('instructions.column.groupsFilter')"
              chips
              small-chips
              hide-details
            ></v-autocomplete>
          </v-col>

          <v-col>
            <due-date-color-diagram />
          </v-col>
          <v-col cols="auto" class="text-end">
            <employee-assign-instructions
              v-if="
                role(
                  'admin',
                  'departmentManager',
                  'manager',
                  'safetyRepresentative'
                )
              "
              :id="employee.id"
              :employeeInstructions.sync="employee.instructions"
              @refresh="$apollo.queries.employee.refresh()"
            />
          </v-col>
        </v-row>

        <employee-instructions
          v-if="!$apollo.loading"
          :employee="employee"
          :instructions.sync="employee.instructions"
          :tags="employee.tags"
          :modes="employee.modes"
          :instructionGroupFilter="instructionGroupFilter"
          @refresh="$apollo.queries.employee.refresh()"
          :onlyConfirmable="false"
        />

        <employee-risk-analysis-reading
          :employee="employee"
          @risk-analyses="updateUnsignedRiskAnalysisAlert"
        />

        <employee-ext-training-table
          :employee="employee"
          :employeeId="employee.id"
          :firstName="employee.firstName"
          :lastName="employee.lastName"
          @refresh="$apollo.queries.employee.refresh()"
        />

        <employee-health-check
          v-if="
            role(
              'admin',
              'departmentManager',
              'manager',
              'safetyRepresentative'
            ) ||
              (me && employee.id === me.id)
          "
          :employee="employee"
          @refresh="$apollo.queries.employee.refresh()"
        />

        <employee-tools :employee="employee" />
      </template>

      <template v-else>
        <v-toolbar flat max-height="64" color="transparent">
          <v-subheader class="title mt-2 text-uppercase">{{
            $t("profile.subheader.history")
          }}</v-subheader>
          <v-spacer></v-spacer>
          <v-btn
            small
            class="primary accent-4 white--text mr-2"
            @click="print('printEmployeeHistorySection', id)"
          >
            <v-icon small left>print</v-icon>
            {{ $t("profile.history.print") }}
          </v-btn>
          <employee-toggle-history-view
            v-if="history"
            :state.sync="historyTable"
          />
        </v-toolbar>
        <div id="printEmployeeHistorySection">
          <div class="d-none">
            {{
              $t("profile.history.printHistoryHeader", {
                nameId: $options.filters.nameId(employee),
              })
            }}
          </div>

          <employee-history-table v-if="historyTable" :id="id" />
          <employee-history-tree-view v-else :id="id" />
        </div>
      </template>
    </v-layout>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import EmployeeQuery from "@/graphql/Employee.gql";
import InstructionGroupsQuery from "@/graphql/queries/InstructionGroups.gql";

import EmployeeMentorship from "@/components/EmployeeMentorship";
import EmployeeHistoryTreeView from "@/components/EmployeeHistory/TreeView";
import EmployeeHistoryTable from "@/components/EmployeeHistory/Table";
import EmployeeInstructions from "@/components/EmployeeInstructions";
import BackButton from "@/components/control/BackButton";
import EmployeeToggleHistoryView from "@/components/control/EmployeeToggleHistoryView";
import EmployeeAssignInstructions from "@/components/EmployeeAssignInstructions";
import EmployeeExtTrainingTable from "@/components/employee/EmployeeExtTrainingTable";
import EmployeeHealthCheck from "@/components/employee/EmployeeHealthCheck";
import MentorshipInfo from "@/components/mentorship/MentorshipInfo.vue";
import EmployeeAlerts from "@/components/employee/EmployeeAlerts.vue";
import DueDateColorDiagram from "@/components/employee/DueDateColorDiagram";
import EmployeeProfileData from "@/components/employee/EmployeeProfileData";
import UnstartedMentorships from "@/components/employee/UnstartedMentorships.vue";
import EmployeeRiskAnalysisReading from "@/components/employee/EmployeeRiskAnalysisReading.vue";
import EmployeeTools from "@/components/employee/EmployeeTools.vue";
import ProfileNavigation from "@/components/control/ProfileNavigation.vue";

import { getMissingParentTags } from "@/utils/common.js";
import { print } from "@/utils/print";

export default {
  components: {
    BackButton,
    EmployeeMentorship,
    EmployeeInstructions,
    EmployeeHistoryTable,
    EmployeeHistoryTreeView,
    EmployeeToggleHistoryView,
    EmployeeAssignInstructions,
    EmployeeExtTrainingTable,
    MentorshipInfo,
    EmployeeAlerts,
    DueDateColorDiagram,
    EmployeeProfileData,
    UnstartedMentorships,
    EmployeeHealthCheck,
    EmployeeRiskAnalysisReading,
    ProfileNavigation,
    EmployeeTools,
  },
  props: ["id"],
  data() {
    return {
      instructionGroupFilter: [],
      employee: {
        role: "employee",
      },
      history: false,
      historyTable: true,
      riskAnalyses: [],
    };
  },
  computed: {
    ...mapGetters(["role"]),
    ...mapState(["me"]),

    showBackButton() {
      return this.$router.currentRoute.name !== "profile";
    },
  },
  methods: {
    print(id, title) {
      print(id, title);
    },
    updateUnsignedRiskAnalysisAlert(data) {
      this.riskAnalyses = data;
    },
  },
  apollo: {
    employee: {
      query: EmployeeQuery,
      variables() {
        return {
          id: this.id,
        };
      },
      update(data) {
        data.employee.missingParentTags = getMissingParentTags(data.employee);
        return data.employee;
      },
      skip() {
        return !this.id;
      },
    },
    instructionGroups: {
      query: InstructionGroupsQuery,
      update(data) {
        return data.groups;
      },
    },
  },
};
</script>

<style>
@media print {
  @page {
    size: landscape;
  }
}
</style>
